import type { UploadHistoryTableColumnSetting } from '../../types/claims';
import type { SortingState } from '../../types/table';
import { parseSortingState } from './sortingStorage';
import { readTenantFilters, writeTenantFilters } from './filtersTenantStorage';

// One localStorage blob for the Claims dashboard's upload history filters
// (quick date filter / custom range, column filters), table sort and column settings.
// Stored per tenant and user, see filtersTenantStorage.
export const DASHBOARD_FILTERS_STORAGE_KEY = 'filters:dashboard';

export interface PersistedDashboardFilters {
  /** Quick filter value ('today', 'this_month', ...); its range is recomputed on load. */
  selectedDateFilter: string | null;
  /** ISO timestamps of a custom range; only set when no quick filter is active. */
  customDateRange: { start: string; end: string } | null;
  statusFilter: string | null;
  ediTypeFilter: string | null;
  fileNameFilter: string | null;
  claimNumberFilter: string | null;
  columnSettings: UploadHistoryTableColumnSetting[] | null;
  /** undefined = nothing saved (the store keeps its default sort). */
  sorting?: SortingState;
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  value != null && typeof value === 'object' && !Array.isArray(value);

const asString = (value: unknown): string | null =>
  typeof value === 'string' && value ? value : null;

const parseCustomDateRange = (value: unknown): PersistedDashboardFilters['customDateRange'] => {
  if (!isPlainObject(value)) return null;
  const start = asString(value.start);
  const end = asString(value.end);
  if (!start || !end) return null;
  if (isNaN(new Date(start).getTime()) || isNaN(new Date(end).getTime())) return null;
  return { start, end };
};

const parseColumnSettings = (value: unknown): UploadHistoryTableColumnSetting[] | null =>
  Array.isArray(value) ? (value as UploadHistoryTableColumnSetting[]) : null;

export const loadPersistedDashboardFilters = (scope: string | null): PersistedDashboardFilters => {
  const saved = typeof window !== 'undefined' ? readTenantFilters(DASHBOARD_FILTERS_STORAGE_KEY, scope) : null;
  const blob = isPlainObject(saved) ? saved : {};

  return {
    selectedDateFilter: asString(blob.selectedDateFilter),
    customDateRange: parseCustomDateRange(blob.customDateRange),
    statusFilter: asString(blob.statusFilter),
    ediTypeFilter: asString(blob.ediTypeFilter),
    fileNameFilter: asString(blob.fileNameFilter),
    claimNumberFilter: asString(blob.claimNumberFilter),
    columnSettings: parseColumnSettings(blob.columnSettings),
    sorting: parseSortingState(blob.sorting),
  };
};

export const savePersistedDashboardFilters = (scope: string | null, filters: PersistedDashboardFilters): void => {
  writeTenantFilters(DASHBOARD_FILTERS_STORAGE_KEY, scope, filters);
};
